import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { createHttpClient } from '@shared/api/httpClient';
import { canDownloadClientArtifacts } from '@shared/auth/cognito';
import { formatLocalDateTime, parseDateTime } from '@shared/utils/dateTime';
import { statusPillClassName } from '@shared/utils/statusPill';
import {
  ApiRecord,
  asArray,
  asRecord,
  countSignedDocuments,
  friendlyStatus,
  latestPackageFromResponse,
  numberValue,
  packageCounts,
  packageId,
  packageName,
  packageScopeReviewMessages,
  hasPackageScopeReviewContext,
  packageStage,
  packageStageLabel,
  packageStatus,
  packageType,
  packageUpdatedAt,
  recordsFromResponse,
  text,
  valueOrDash,
} from './packageUi';
import { openPackageDownload, type PackageDownloadStatus } from './packageDownload';
import { requestPackageReportPreviewUrl } from './packageReportPreview';
import { useClientAiAssistantPageScope } from '../components/ClientAiAssistantDrawer';
import { useClientAccount } from '../account/AccountContext';


type IconName = 'shield' | 'check' | 'pass' | 'partial' | 'fail' | 'na' | 'total' | 'document' | 'calendar' | 'workflow' | 'folder' | 'list' | 'download' | 'external' | 'eye';

function DashboardIcon({ name, className = '' }: { name: IconName; className?: string }) {
  const common = {
    className: `dashboard-icon ${className}`.trim(),
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };

  switch (name) {
    case 'shield':
      return <svg {...common}><path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z" /><path d="M12 8l1.1 2.3 2.5.4-1.8 1.8.4 2.5-2.2-1.2-2.2 1.2.4-2.5-1.8-1.8 2.5-.4L12 8z" /></svg>;
    case 'check':
      return <svg {...common}><path d="M20 6L9 17l-5-5" /></svg>;
    case 'pass':
      return <svg {...common}><path d="M20 6L9 17l-5-5" /></svg>;
    case 'partial':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 3v18" /></svg>;
    case 'fail':
      return <svg {...common}><path d="M18 6L6 18" /><path d="M6 6l12 12" /></svg>;
    case 'na':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="M9 9l6 6" /><path d="M15 9l-6 6" /></svg>;
    case 'total':
      return <svg {...common}><path d="M12 3l8 4-8 4-8-4 8-4z" /><path d="M4 12l8 4 8-4" /><path d="M4 17l8 4 8-4" /></svg>;
    case 'document':
      return <svg {...common}><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" /><path d="M14 3v6h6" /><path d="M9 15h6" /><path d="M9 11h2" /></svg>;
    case 'calendar':
      return <svg {...common}><rect x="3" y="4" width="18" height="17" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>;
    case 'workflow':
      return <svg {...common}><path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1.2 1.2" /><path d="M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1.2-1.2" /></svg>;
    case 'folder':
      return <svg {...common}><path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></svg>;
    case 'list':
      return <svg {...common}><path d="M8 6h13" /><path d="M8 12h13" /><path d="M8 18h13" /><path d="M3 6h.01" /><path d="M3 12h.01" /><path d="M3 18h.01" /></svg>;
    case 'download':
      return <svg {...common}><path d="M12 3v12" /><path d="M7 10l5 5 5-5" /><path d="M5 21h14" /></svg>;
    case 'external':
      return <svg {...common}><path d="M14 3h7v7" /><path d="M10 14L21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" /></svg>;
    case 'eye':
      return <svg {...common}><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6S2 12 2 12z" /><circle cx="12" cy="12" r="2.5" /></svg>;
  }
}

function IconButtonContent({ icon, children }: { icon: IconName; children: ReactNode }) {
  return <><DashboardIcon name={icon} className="button-icon" />{children}</>;
}


type LoadState = {
  loading: boolean;
  latestTechnicalPackage: ApiRecord;
  latestFinalAuditorPackage: ApiRecord;
  currentReadinessPackage: ApiRecord;
  actionsRequired: ApiRecord[];
  signedDocumentCount: number;
  clientCompanyName: string;
  clientId: string;
  clientCmmcLevel: string;
  error?: string;
};

function metricCard(label: string, value: string | number, helper?: string, className = '', icon?: IconName) {
  return (
    <article className={`metric-card ${className}`.trim()}>
      {icon && <div className="metric-icon"><DashboardIcon name={icon} /></div>}
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        {helper && <small>{helper}</small>}
      </div>
    </article>
  );
}

function hasPackage(pkg: ApiRecord): boolean {
  return Boolean(packageId(pkg) || packageName(pkg) !== 'Evidence package');
}

function requiredClientIdentityFromResponse(response: unknown): { companyName: string; clientId: string } {
  const root = asRecord(response);
  const organization = asRecord(root.client);
  const companyName = text(organization, 'companyName');
  const clientId = text(organization, 'clientId');

  if (!companyName) {
    throw new Error('Organization identity is missing companyName from /client/packages/latest.');
  }
  if (!clientId) {
    throw new Error('Organization identity is missing routing key from /client/packages/latest.');
  }

  return { companyName, clientId };
}

function readinessBadgeText(pkg: ApiRecord): string {
  const status = packageStatus(pkg);
  if (status) return status;
  return packageType(pkg).toUpperCase() === 'FINAL_AUDITOR_PACKAGE' ? 'Final auditor package ready' : 'Package status unavailable';
}

function clientIdentityBanner(
  organizationName: string,
  organizationSlug: string,
  accountDisplayName: string,
  accountId: string,
  region: string,
  cmmcLevel: string,
  currentPackage: ApiRecord,
) {
  const status = readinessBadgeText(currentPackage);
  const isFinal = packageType(currentPackage).toUpperCase() === 'FINAL_AUDITOR_PACKAGE';

  return (
    <section className="client-identity-banner" aria-label="Organization and selected AWS environment">
      <div className="client-identity-left">
        <div className="client-logo-placeholder" aria-hidden="true">
          <DashboardIcon name="shield" />
        </div>
        <div className="client-identity-copy">
          <span>Organization</span>
          <strong>{organizationName}</strong>
          <div className="client-identity-meta">
            <span>{organizationSlug}</span>
          </div>
          <span>Selected AWS environment</span>
          <strong>{accountDisplayName || `AWS ${accountId}`}</strong>
          <div className="client-identity-meta">
            <span>AWS {accountId}</span>
            <span>{region || 'Region unavailable'}</span>
            <span>{cmmcLevel || 'CMMC level unavailable'}</span>
          </div>
        </div>
      </div>
      <div className={`client-ready-callout ${isFinal ? 'client-ready-callout-success' : 'client-ready-callout-warning'}`}>
        <div className="client-ready-icon"><DashboardIcon name={isFinal ? 'check' : 'partial'} /></div>
        <div>
          <strong>{status}</strong>
          <span>{isFinal ? 'This AWS environment is audit-ready.' : 'A final auditor package has not been created for this AWS environment yet.'}</span>
        </div>
      </div>
    </section>
  );
}

function currentReadinessSourceLabel(pkg: ApiRecord): string {
  const type = packageType(pkg).toUpperCase();
  if (type === 'FINAL_AUDITOR_PACKAGE') return 'Source: Final Auditor Package';
  if (type === 'TECHNICAL_EVIDENCE_PACKAGE') return 'Source: Technical Baseline';
  return 'Source: Latest indexed package';
}

function currentReadinessTitle(pkg: ApiRecord): string {
  return packageType(pkg).toUpperCase() === 'FINAL_AUDITOR_PACKAGE'
    ? 'Current Readiness'
    : 'Latest Technical Evidence Package';
}

function packageDetailPath(pkg: ApiRecord, accountPath: (path?: string) => string): string {
  const id = packageId(pkg);
  return accountPath(id ? `/evidence-packages/${encodeURIComponent(id)}` : '/evidence-packages');
}

function workflowId(pkg: ApiRecord): string {
  return text(pkg, 'jobId', 'workflowId', 'workflowJobId', 'sourceJobId', 'parentJobId', 'executionId');
}


function packageDate(pkg: ApiRecord): Date | null {
  return parseDateTime(packageUpdatedAt(pkg));
}


function packageUpdatedAtDisplay(pkg: ApiRecord): string {
  return formatLocalDateTime(packageUpdatedAt(pkg));
}

function readinessPercent(pkg: ApiRecord): number {
  const counts = packageCounts(pkg);
  if (!counts.total) return 0;
  return Math.round((((counts.pass || 0) + (counts.notApplicable || 0)) / counts.total) * 1000) / 10;
}

function readinessPercentLabel(pkg: ApiRecord): string {
  const value = readinessPercent(pkg);
  return `${Number.isInteger(value) ? value.toFixed(0) : value.toFixed(1)}%`;
}

function isTechnicalBaselineNewer(finalPackage: ApiRecord, technicalPackage: ApiRecord): boolean {
  if (!hasPackage(finalPackage) || !hasPackage(technicalPackage)) return false;
  const finalDate = packageDate(finalPackage);
  const technicalDate = packageDate(technicalPackage);
  return Boolean(finalDate && technicalDate && technicalDate.getTime() > finalDate.getTime());
}

function readinessProgress(pkg: ApiRecord) {
  const percent = readinessPercent(pkg);
  const boundedPercent = Math.max(0, Math.min(100, percent));
  return (
    <div className="readiness-progress-card">
      <div className="readiness-progress-header">
        <div>
          <span>Readiness coverage</span>
          <strong>{readinessPercentLabel(pkg)}</strong>
        </div>
        <small>PASS and N/A controls count as resolved for this visual indicator.</small>
      </div>
      <div className="readiness-progress-track" aria-label={`Readiness coverage ${readinessPercentLabel(pkg)}`}>
        <div className="readiness-progress-fill" style={{ width: `${boundedPercent}%` }} />
      </div>
    </div>
  );
}

function staleFinalPackageWarning(finalPackage: ApiRecord, technicalPackage: ApiRecord) {
  if (!isTechnicalBaselineNewer(finalPackage, technicalPackage)) return null;

  return (
    <div className="dashboard-warning-banner" role="status">
      <div>
        <strong>Newer technical baseline available</strong>
        <p>
          The latest technical baseline was generated after the current final auditor package.
          The final auditor package may not reflect the most recent technical evidence run.
        </p>
      </div>
      <div className="warning-facts-grid">
        <div>
          <span>Final auditor package created</span>
          <strong>{packageUpdatedAtDisplay(finalPackage)}</strong>
        </div>
        <div>
          <span>Latest technical baseline generated</span>
          <strong>{packageUpdatedAtDisplay(technicalPackage)}</strong>
        </div>
        {workflowId(technicalPackage) && (
          <div>
            <span>Technical workflow ID</span>
            <strong>{workflowId(technicalPackage)}</strong>
          </div>
        )}
      </div>
    </div>
  );
}


function ScopeReviewContext({ pkg }: { pkg: ApiRecord }) {
  if (!hasPackageScopeReviewContext(pkg)) return null;
  return (
    <div className="scope-review-context">
      <strong>Resource scope review</strong>
      {packageScopeReviewMessages(pkg).map((message) => (
        <p className="muted-text" key={message}>{message}</p>
      ))}
    </div>
  );
}

function readinessSummaryCard(pkg: ApiRecord, latestTechnicalPackage: ApiRecord, accountPath: (path?: string) => string) {
  const counts = packageCounts(pkg);
  const id = packageId(pkg);
  const isFinal = packageType(pkg).toUpperCase() === 'FINAL_AUDITOR_PACKAGE';

  return (
    <article className="card dashboard-hero-card">
      <div className="section-title-row">
        <div>
          <h3>{currentReadinessTitle(pkg)}</h3>
          <p className="muted-text">
            {isFinal
              ? 'This summary reflects the latest final auditor package after signed client evidence and scoping decisions have been processed.'
              : 'No final auditor package is available yet, so this summary reflects the latest technical baseline.'}
          </p>
          <p className="muted-text">
            {currentReadinessSourceLabel(pkg)} · {valueOrDash(packageName(pkg))}
          </p>
        </div>
        <span className={statusPillClassName(packageStatus(pkg))}>{packageStatus(pkg)}</span>
      </div>

      {isFinal && staleFinalPackageWarning(pkg, latestTechnicalPackage)}
      {readinessProgress(pkg)}

      <div className="metric-grid readiness-metrics">
        {metricCard('PASS', counts.pass, 'Controls supported', 'metric-success', 'pass')}
        {metricCard('PARTIAL', counts.partial, 'Needs more evidence', 'metric-warning', 'partial')}
        {metricCard('FAIL', counts.fail, 'Requires remediation', 'metric-error', 'fail')}
        {metricCard('N/A', counts.notApplicable, 'Scoped out or declared', 'metric-muted', 'na')}
        {metricCard('TOTAL', counts.total, 'Total controls', 'metric-blue', 'total')}
      </div>

      <ScopeReviewContext pkg={pkg} />

      <div className="details-grid">
        <div className="detail-row">
          <DashboardIcon name="document" className="detail-icon" />
          <span>Package stage</span>
          <strong><span className={statusPillClassName(packageStage(pkg) || packageStageLabel(pkg))}>{valueOrDash(packageStageLabel(pkg))}</span></strong>
        </div>
        <div className="detail-row">
          <DashboardIcon name="document" className="detail-icon" />
          <span>CMMC level</span>
          <strong>{valueOrDash(text(pkg, 'cmmcLevel', 'cmmc_level', 'level'))}</strong>
        </div>
        <div className="detail-row">
          <DashboardIcon name="calendar" className="detail-icon" />
          <span>{isFinal ? 'Final package created' : 'Technical baseline generated'}</span>
          <strong>{packageUpdatedAtDisplay(pkg)}</strong>
        </div>
        {workflowId(pkg) && (
          <div className="detail-row">
            <DashboardIcon name="workflow" className="detail-icon" />
            <span>Workflow ID</span>
            <strong>{workflowId(pkg)}</strong>
          </div>
        )}
      </div>

      <div className="card-actions">
        <Link className="secondary-button" to={accountPath(id ? `/evidence-packages/${encodeURIComponent(id)}` : '/evidence-packages')}>
          <IconButtonContent icon="folder">Open package record</IconButtonContent>
        </Link>
      </div>
    </article>
  );
}

function packageAccessCard(
  finalPackage: ApiRecord,
  actionsRequiredCount: number,
  signedDocumentCount: number,
  downloadStatus: PackageDownloadStatus,
  onDownload: (pkg: ApiRecord) => void,
  onPreviewReport: (pkg: ApiRecord) => void,
  canDownloadArtifacts: boolean,
  accountPath: (path?: string) => string,
) {
  const id = packageId(finalPackage);
  const hasFinalPackage = hasPackage(finalPackage);
  const isPreparing = downloadStatus.state === 'preparing' && downloadStatus.packageId === id;

  return (
    <article className="card package-access-card">
      <div className="section-title-row">
        <div>
          <h3>Package Access and Client Actions</h3>
          <p className="muted-text">
            Use this section to open the assessor-ready package record and track whether client action is still needed.
          </p>
        </div>
        <span className={`status-pill ${hasFinalPackage ? 'status-pill-success' : 'status-pill-warning'}`}>
          {hasFinalPackage ? 'Auditor package available' : 'Auditor package not created'}
        </span>
      </div>

      <div className="package-access-summary">
        <div className="package-access-artifact">
          <div className="artifact-icon"><DashboardIcon name="document" /></div>
          <div>
          <span>Final auditor package</span>
          <strong>{hasFinalPackage ? packageName(finalPackage) : 'Not available yet'}</strong>
          {hasFinalPackage && <small>Created {packageUpdatedAtDisplay(finalPackage)}</small>}
          </div>
        </div>
        <div>
          <span>Client actions</span>
          <strong>{actionsRequiredCount}</strong>
        </div>
        <div>
          <span>Signed documents uploaded</span>
          <strong>{signedDocumentCount}</strong>
        </div>
      </div>

      <p className="muted-text">
        {hasFinalPackage
          ? canDownloadArtifacts
            ? 'The final auditor package is the controlled package intended for assessor review. Client admins can open the package record or download the authorized artifact.'
            : 'The final auditor package is available for review. Downloads are admin-only, but you can open the package record to review controls and document details.'
          : 'A final auditor package will appear here after signed client evidence and scoping decisions are processed.'}
      </p>

      <div className="card-actions">
        {id && <Link className="secondary-button" to={packageDetailPath(finalPackage, accountPath)}><IconButtonContent icon="folder">Open package record</IconButtonContent></Link>}
        {id && (
          <button className="secondary-button" type="button" onClick={() => onPreviewReport(finalPackage)}>
            <IconButtonContent icon="eye">Preview audit report</IconButtonContent>
          </button>
        )}
        {id && canDownloadArtifacts ? (
          <button className="primary-button" type="button" onClick={() => onDownload(finalPackage)} disabled={isPreparing}>
            <IconButtonContent icon="download">{isPreparing ? 'Preparing download...' : 'Download auditor package'}</IconButtonContent>
          </button>
        ) : id ? (
          <span className="status-pill status-pill-muted" title="Downloads are available only to client administrators.">Admin only</span>
        ) : (
          <button className="primary-button" type="button" disabled>Download unavailable</button>
        )}
      </div>
    </article>
  );
}

function technicalBaselineCard(
  pkg: ApiRecord,
  finalPackage: ApiRecord,
  downloadStatus: PackageDownloadStatus,
  onDownload: (pkg: ApiRecord) => void,
  accountPath: (path?: string) => string,
) {
  if (!hasPackage(pkg)) return null;
  const counts = packageCounts(pkg);
  const id = packageId(pkg);
  const isPreparing = downloadStatus.state === 'preparing' && downloadStatus.packageId === id;
  const newerThanFinal = isTechnicalBaselineNewer(finalPackage, pkg);

  return (
    <article className="card technical-baseline-card">
      <div className="section-title-row">
        <div>
          <h3>Latest Technical Baseline</h3>
          <p className="muted-text">{valueOrDash(packageName(pkg))}</p>
        </div>
        <span className={statusPillClassName(packageStatus(pkg))}>{packageStatus(pkg)}</span>
      </div>

      {newerThanFinal && (
        <div className="technical-newer-callout">
          <strong>Newer than final auditor package</strong>
          <p>
            This technical baseline was generated after the current final auditor package.
            Generate a new final auditor package when you are ready to create an updated assessor-ready package.
          </p>
        </div>
      )}

      {readinessProgress(pkg)}

      <div className="compact-readiness-row readiness-badge-row">
        <span className="readiness-badge readiness-badge-success">PASS <strong>{counts.pass}</strong></span>
        <span className="readiness-badge readiness-badge-warning">PARTIAL <strong>{counts.partial}</strong></span>
        <span className="readiness-badge readiness-badge-error">FAIL <strong>{counts.fail}</strong></span>
        <span className="readiness-badge readiness-badge-muted">N/A <strong>{counts.notApplicable}</strong></span>
        <span className="readiness-badge readiness-badge-blue">TOTAL <strong>{counts.total}</strong></span>
      </div>

      <ScopeReviewContext pkg={pkg} />

      <div className="details-grid">
        <div className="detail-row">
          <DashboardIcon name="document" className="detail-icon" />
          <span>Stage</span>
          <strong><span className={statusPillClassName(packageStage(pkg) || packageStageLabel(pkg))}>{valueOrDash(packageStageLabel(pkg))}</span></strong>
        </div>
        <div className="detail-row">
          <DashboardIcon name="document" className="detail-icon" />
          <span>CMMC level</span>
          <strong>{valueOrDash(text(pkg, 'cmmcLevel', 'cmmc_level', 'level'))}</strong>
        </div>
        <div className="detail-row">
          <DashboardIcon name="calendar" className="detail-icon" />
          <span>Generated</span>
          <strong>{packageUpdatedAtDisplay(pkg)}</strong>
        </div>
        {workflowId(pkg) && (
          <div className="detail-row">
            <DashboardIcon name="workflow" className="detail-icon" />
            <span>Workflow ID</span>
            <strong>{workflowId(pkg)}</strong>
          </div>
        )}
      </div>

      <div className="card-actions">
        <Link className="secondary-button" to={accountPath('/actions-required')}><IconButtonContent icon="list">Review action items</IconButtonContent></Link>
        <Link className="secondary-button" to={packageDetailPath(pkg, accountPath)}><IconButtonContent icon="external">View technical package</IconButtonContent></Link>
      </div>
    </article>
  );
}

export default function DashboardPage() {
  const { accountPath, accounts, selectedAccount, selectAccount } = useClientAccount();
  const api = useMemo(() => createHttpClient({ portal: 'client' }), []);
  const [state, setState] = useState<LoadState>({
    loading: true,
    latestTechnicalPackage: {},
    latestFinalAuditorPackage: {},
    currentReadinessPackage: {},
    actionsRequired: [],
    signedDocumentCount: 0,
    clientCompanyName: '',
    clientId: '',
    clientCmmcLevel: '',
  });
  const [downloadStatus, setDownloadStatus] = useState<PackageDownloadStatus>({ state: 'idle' });
  const [reportPreview, setReportPreview] = useState<{ loading: boolean; url?: string; error?: string }>({ loading: false });
  const canDownloadArtifacts = canDownloadClientArtifacts();
  const currentReadinessPackageId = packageId(state.currentReadinessPackage);
  const currentReadinessPackageName = packageName(state.currentReadinessPackage);
  const currentReadinessPackageType = packageType(state.currentReadinessPackage) || undefined;
  const aiScope = useMemo(
    () => ({
      packageId: currentReadinessPackageId || undefined,
      selectedPackageId: currentReadinessPackageId || undefined,
      packageName: currentReadinessPackageId ? currentReadinessPackageName : undefined,
      packageType: currentReadinessPackageType,
      pageType: 'CLIENT_DASHBOARD',
      sourceLabel: 'Client readiness dashboard',
      pageContext: {
        pageType: 'CLIENT_DASHBOARD',
        packageId: currentReadinessPackageId || undefined,
        packageName: currentReadinessPackageId ? currentReadinessPackageName : undefined,
        packageType: currentReadinessPackageType,
        sourceLabel: 'Client readiness dashboard',
      },
    }),
    [currentReadinessPackageId, currentReadinessPackageName, currentReadinessPackageType],
  );
  useClientAiAssistantPageScope(aiScope);

  async function handlePreviewReport(pkg: ApiRecord) {
    setReportPreview({ loading: true });
    try {
      const response = await requestPackageReportPreviewUrl(api, pkg);
      setReportPreview({ loading: false, url: response.previewUrl });
    } catch (error) {
      setReportPreview({
        loading: false,
        error: error instanceof Error ? error.message : 'Unable to preview the audit report.',
      });
    }
  }

  async function handleDownload(pkg: ApiRecord) {
    const id = packageId(pkg);
    if (!canDownloadArtifacts) {
      setDownloadStatus({
        packageId: id,
        state: 'error',
        message: 'Downloads are available only to client administrators. Your current role can still review package status and details.',
      });
      return;
    }
    setDownloadStatus({ packageId: id, state: 'preparing' });
    try {
      await openPackageDownload(api, pkg);
      setDownloadStatus({ state: 'idle' });
    } catch (error) {
      setDownloadStatus({
        packageId: id,
        state: 'error',
        message: error instanceof Error ? error.message : 'Unable to prepare package download.',
      });
    }
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [latestResponse, actionsResponse] = await Promise.all([
          api.get<unknown>('/client/packages/latest'),
          api.get<unknown>('/client/actions-required'),
        ]);

        const technicalPackage = latestPackageFromResponse(latestResponse, 'technical');
        const finalPackage = latestPackageFromResponse(latestResponse, 'final');
        const currentReadinessPackage = hasPackage(finalPackage) ? finalPackage : technicalPackage;
        const clientIdentity = requiredClientIdentityFromResponse(latestResponse);
        const actionsRequired = recordsFromResponse(actionsResponse, ['actions', 'actionsRequired', 'items', 'packages', 'documents', 'controls']);
        const actionDocuments = recordsFromResponse(actionsResponse, ['documents', 'actionDocuments']);
        const signedDocumentCount = Number(text(actionsResponse as ApiRecord, 'signedDocumentCount', 'signedDocumentsUploaded'))
          || countSignedDocuments(asArray((actionsResponse as ApiRecord).documents))
          || countSignedDocuments(actionDocuments)
          || numberValue(finalPackage, 'completedEvidenceDocumentCount', 'signedDocumentCount', 'signedDocumentsUploaded')
          || 0;

        if (!cancelled) {
          setState({
            loading: false,
            latestTechnicalPackage: technicalPackage,
            latestFinalAuditorPackage: finalPackage,
            currentReadinessPackage,
            actionsRequired,
            signedDocumentCount,
            clientCompanyName: clientIdentity.companyName,
            clientId: clientIdentity.clientId,
            clientCmmcLevel: selectedAccount?.cmmcLevel || text(asRecord(asRecord(latestResponse).account), 'cmmcLevel'),
          });
        }
      } catch (error) {
        if (!cancelled) {
          setState((current) => ({
            ...current,
            loading: false,
            error: error instanceof Error ? error.message : 'Unable to load dashboard package status.',
          }));
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [api, selectedAccount?.accountId, selectedAccount?.cmmcLevel]);

  return (
    <div className="page-stack">
      <div className="page-heading">
        <h2>Dashboard</h2>
        <p>Readiness status, evidence progress, and required actions for the selected AWS environment.</p>
      </div>

      {selectedAccount && accounts.length > 1 && (
        <section className="card dashboard-account-switch-card" aria-label="Switch AWS environment">
          <div>
            <span className="dashboard-account-switch-kicker">Current AWS environment</span>
            <strong>{selectedAccount.displayName || `AWS ${selectedAccount.accountId}`}</strong>
            <p className="muted-text">Switching reloads the client workspace so account-specific data, page state, downloads, and AI context remain isolated.</p>
          </div>
          <label>
            <span>Switch environment</span>
            <select value={selectedAccount.accountId} onChange={(event) => selectAccount(event.target.value)}>
              {accounts.map((account) => (
                <option key={account.accountId} value={account.accountId}>
                  {account.displayName || `AWS ${account.accountId}`} · {account.accountId}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {state.loading && <article className="card"><p>Loading CMMC package dashboard...</p></article>}
      {state.error && <article className="card"><p className="error-text">{state.error}</p></article>}

      {!state.loading && !state.error && (
        <>
          {clientIdentityBanner(
            state.clientCompanyName,
            state.clientId,
            selectedAccount?.displayName || '',
            selectedAccount?.accountId || '',
            selectedAccount?.region || '',
            state.clientCmmcLevel,
            state.currentReadinessPackage,
          )}

          {readinessSummaryCard(state.currentReadinessPackage, state.latestTechnicalPackage, accountPath)}

          <div className="dashboard-two-column-grid">
            {packageAccessCard(
              state.latestFinalAuditorPackage,
              state.actionsRequired.length,
              state.signedDocumentCount,
              downloadStatus,
              handleDownload,
              handlePreviewReport,
              canDownloadArtifacts,
              accountPath,
            )}

            {technicalBaselineCard(
              state.latestTechnicalPackage,
              state.latestFinalAuditorPackage,
              downloadStatus,
              handleDownload,
              accountPath,
            )}
          </div>

          {downloadStatus.state === 'error' && downloadStatus.message && (
            <article className="card"><p className="error-text">{downloadStatus.message}</p></article>
          )}

          {(reportPreview.loading || reportPreview.url || reportPreview.error) && (
            <div className="report-preview-backdrop" role="presentation" onMouseDown={() => setReportPreview({ loading: false })}>
              <section className="report-preview-dialog" role="dialog" aria-modal="true" aria-label="Audit report preview" onMouseDown={(event) => event.stopPropagation()}>
                <div className="report-preview-header">
                  <div>
                    <h3>Audit Report Preview</h3>
                    <p className="muted-text">{packageName(state.latestFinalAuditorPackage)}</p>
                  </div>
                  <button className="secondary-button" type="button" onClick={() => setReportPreview({ loading: false })}>Close</button>
                </div>
                {reportPreview.loading && <p className="report-preview-message">Preparing report preview...</p>}
                {reportPreview.error && <p className="error-text report-preview-message">{reportPreview.error}</p>}
                {reportPreview.url && (
                  <iframe className="report-preview-frame" src={reportPreview.url} title="Final audit readiness report" />
                )}
              </section>
            </div>
          )}
        </>
      )}
    </div>
  );
}
